/* eslint-disable @typescript-eslint/no-explicit-any */
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit'
import { AxiosInstance } from 'axios'
import { TRootState } from '..'
import allApi from '../../api'
import {
  Applicant,
  CreditApplication,
  EditCreditApplicationSinAndLoanAmount,
  Worksheet,
} from '../../schemas/PrequalificationSchema'
import { AvailableCredit } from '../../types/AvailableCredit'
import { ComputeAmountForFrequencyDto } from '../../types/ComputeAmountForFrequencyDto'
import { ComputedAmountForFrequencyDto } from '../../types/ComputedAmountForFrequencyDto'
import { FilteredCreditApplication } from '../../types/FilteredCreditApplication'
import { FilteredWorksheet } from '../../types/FilteredWorksheet'
import { MerchantDto } from '../../types/Merchant'
import { PersonalLoanFundingComputedDto } from '../../types/PersonalLoanFundingComputedDto'
import { PersonalLoanFundingInfoDto } from '../../types/PersonalLoanFundingInfoDto'
import { PromotionCodeDto, ValidatePromotionCode } from '../../types/PromoCode'
import { RefinancingCreditApplication } from '../../types/RefinancingCreditApplication'
import { RequiredDocument } from '../../types/RequiredDocument'
import { apiStateActions } from '../ApiStateStore'
import { appActions } from '../AppStore'
import { makeGetById, makeSave } from '../Effects/standard-side-effects'
import {
  COMPUTE_AMOUNT_FREQUENCY,
  COMPUTE_PERSONAL_LOAN_FUNDING,
  GET_AVAILABLE_CREDIT,
  GET_FILTERED_CREDIT_APPLICATION,
  GET_MERCHANT,
  GET_REFINANCING_CREDIT_APPLICATION,
  POST_COAPPLICANT,
  POST_DOCUMENT,
  POST_DRAFT_CREDIT_APPLICATION_AND_PREQUALIFY,
  POST_EMAIL_RESUME_LINK,
  POST_PROMO_CODE,
  POST_QUALIFY,
  POST_REFINANCING_CREDIT_APPLICATION,
  POST_RESEND_ELECTRONIC_SIGNATURE,
  PUT_CANCEL_CREDIT_APPLICATION,
  PUT_UPDATE_CREDIT_APPLICATION,
  REMOVE_CO_APPLICANT,
  SAVE_WORKSHEET,
  creditApplicationActions,
  GET_CUSTOMER_FEEDBACK,
  POST_CUSTOMER_FEEDBACK,
} from './creditApplication-store'
import { CustomerFeedback } from '../../types/CustomerFeedback'

export const creditApplicationEffects = {
  postDraftCreditApplicationAndPrequalify: (creditApplication: CreditApplication) =>
    makeSave<CreditApplication, FilteredCreditApplication>(
      POST_DRAFT_CREDIT_APPLICATION_AND_PREQUALIFY,
      allApi.creditApplication.postDraftCreditApplicationAndPrequalify,
      creditApplicationActions.setFilteredCreditApplication,
      creditApplication,
    ),

  getFilteredCreditApplication: (id: string) =>
    makeGetById<FilteredCreditApplication>(
      GET_FILTERED_CREDIT_APPLICATION,
      allApi.creditApplication.getFilteredCreditApplication,
      creditApplicationActions.setFilteredCreditApplication,
      { id },
    ),
  postDocument: (data: FormData, creditApplicationId: string) => {
    return async (
      apiClient: AxiosInstance,
      dispatch: ThunkDispatch<TRootState, undefined, AnyAction>,
    ): Promise<RequiredDocument> => {
      try {
        dispatch(apiStateActions.logApiCallInitiated(POST_DOCUMENT))
        dispatch(appActions.setBusinessErrors([]))

        const ret = await allApi.creditApplication.postDocument(apiClient, data, creditApplicationId)
        dispatch(creditApplicationActions.setRequiredDocument(ret))
        return ret
      } finally {
        dispatch(apiStateActions.logApiCallCompleted(POST_DOCUMENT))
      }
    }
  },
  emailResumeLink: (id: string) =>
    makeSave(POST_EMAIL_RESUME_LINK, allApi.creditApplication.postEmailResumeLink, null, { id }),
  postQualify: (id: string) =>
    makeSave<FilteredCreditApplication>(
      POST_QUALIFY,
      allApi.creditApplication.postQualify,
      creditApplicationActions.setFilteredCreditApplication,
      { id },
    ),
  postCoapplicant: (applicant: Applicant, creditApplicationId: string) =>
    makeSave<Applicant, FilteredCreditApplication>(
      POST_COAPPLICANT,
      allApi.creditApplication.postCoapplicant,
      creditApplicationActions.setFilteredCreditApplication,
      { ...applicant, creditApplicationId } as any,
    ),
  putCancelCreditApplication: (id: string) =>
    makeSave<FilteredCreditApplication>(
      PUT_CANCEL_CREDIT_APPLICATION,
      allApi.creditApplication.putCancelCreditApplication,
      creditApplicationActions.setFilteredCreditApplication,
      { id },
    ),
  removeCoApplicant: (id: string) =>
    makeSave<FilteredCreditApplication>(
      REMOVE_CO_APPLICANT,
      allApi.creditApplication.removeCoApplicant,
      creditApplicationActions.setFilteredCreditApplication,
      { id },
    ),
  putUpdateCreditApplication: (creditApplicationInformation: EditCreditApplicationSinAndLoanAmount) =>
    makeSave<EditCreditApplicationSinAndLoanAmount>(
      PUT_UPDATE_CREDIT_APPLICATION,
      allApi.creditApplication.putUpdateCreditApplication,
      null,
      creditApplicationInformation,
    ),

  createWorksheet: (dto: Worksheet) =>
    makeSave<Worksheet, FilteredWorksheet>(
      SAVE_WORKSHEET,
      allApi.creditApplication.postWorkSheet,
      creditApplicationActions.setWorkSheet,
      dto,
    ),

  updateWorksheet: (dto: Worksheet) =>
    makeSave<Worksheet, FilteredWorksheet>(
      SAVE_WORKSHEET,
      allApi.creditApplication.updateWorksheet,
      creditApplicationActions.setWorkSheet,
      dto,
    ),

  validatePromotionCode: (promoCodeDto: PromotionCodeDto) =>
    makeSave<PromotionCodeDto, ValidatePromotionCode>(
      POST_PROMO_CODE,
      allApi.creditApplication.validatePromotionCode,
      null,
      promoCodeDto,
    ),
  getRefinancingCreditApplication: (id: string) =>
    makeGetById<RefinancingCreditApplication>(
      GET_REFINANCING_CREDIT_APPLICATION,
      allApi.creditApplication.getRefinancingCreditApplication,
      creditApplicationActions.setRefinancingCreditApplication,
      { id },
    ),
  postRefinancingCreditApplication: (refinancingCreditApplication: RefinancingCreditApplication) =>
    makeSave<RefinancingCreditApplication>(
      POST_REFINANCING_CREDIT_APPLICATION,
      allApi.creditApplication.postRefinancingCreditApplication,
      creditApplicationActions.setRefinancingCreditApplication,
      refinancingCreditApplication,
    ),

  getMerchant: (id: string) =>
    makeGetById<MerchantDto>(GET_MERCHANT, allApi.creditApplication.getMerchant, creditApplicationActions.setMerchant, {
      id,
    }),

  resendElectronicSignature: (id: string) =>
    makeSave(POST_RESEND_ELECTRONIC_SIGNATURE, allApi.creditApplication.postResendElectronicSignatureLink, null, {
      id,
    }),
  computePersonalLoanFunding: (data: PersonalLoanFundingInfoDto) =>
    makeSave<PersonalLoanFundingInfoDto, PersonalLoanFundingComputedDto>(
      COMPUTE_PERSONAL_LOAN_FUNDING,
      allApi.creditApplication.computeFunding,
      creditApplicationActions.setFundingComputed,
      data,
    ),
  computeAmountForFrequency: (data: ComputeAmountForFrequencyDto) =>
    makeSave<ComputeAmountForFrequencyDto, ComputedAmountForFrequencyDto>(
      COMPUTE_AMOUNT_FREQUENCY,
      allApi.creditApplication.computeAmount,
      creditApplicationActions.setComputedAmountForFrequency,
      data,
    ),
  getAvailbaleCredit: (id: string) =>
    makeGetById<AvailableCredit>(
      GET_AVAILABLE_CREDIT,
      allApi.creditApplication.getAvailableCredit,
      creditApplicationActions.setAvailableCredit,
      { id },
    ),
  getCustomerFeedback: (creditApplicationId: string) =>
    makeGetById<CustomerFeedback>(
      GET_CUSTOMER_FEEDBACK,
      allApi.creditApplication.getCustomerFeedback,
      creditApplicationActions.setCustomerFeedback,
      { creditApplicationId },
    ),
  postCustomerFeedback: (customerFeedback: CustomerFeedback, creditApplicationId: string) =>
    makeSave<CustomerFeedback>(
      POST_CUSTOMER_FEEDBACK,
      allApi.creditApplication.postCustomerFeedback,
      creditApplicationActions.setJustGaveFeedback,
      {
        ...customerFeedback,
        creditApplicationId,
      } as CustomerFeedback,
    ),
}
